import {
  afterNextRender,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  viewChild,
} from '@angular/core';
import {
  AssessmentPresetItemDto,
  ConfiguredActionItemDto,
  BuildingSectionService,
  CheckpointDetailDto,
  CheckpointItemDto,
  CheckpointService,
  InspectionDetailDto,
  InspectionEntryDetailDto,
  InspectionEntryService,
  RepairTaskMergeDto,
  RepairTaskService,
  TemplateService,
} from '@artemis-software/wr-api';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  filter,
  firstValueFrom,
  map,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { BuildingSectionDetailDto } from '@artemis-software/wr-api/model/buildingSectionDetailDto';
import { NbDialogService, NbMenuItem, NbMenuService, NbToastrService } from '@nebular/theme';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import { CheckpointDialogComponent } from '@components/dialog/checkpoint-dialog/checkpoint-dialog.component';
import {
  TemplateSelectionDialogComponent,
} from '@components/dialog/template-selection-dialog/template-selection-dialog.component';
import { InspectionFormWrapper } from '../inspection-form';
import {
  createInspectionEntryForm,
  getCheckpointString,
  getInspectionEntryStatusFromFormGroup,
  getInspectionEntryStatusText,
  InspectionEntryFormType,
  InspectionFormType,
  isTgaRelevant,
} from '../inspection-util';
import {
  ApplyFromOtherSectionDialogComponent,
} from '@components/dialog/apply-from-other-section-dialog/apply-from-other-section-dialog.component';
import {
  DuplicateSectionDialogComponent,
  DuplicateSectionDialogResult,
} from '@components/dialog/duplicate-section-dialog/duplicate-section-dialog.component';
import { randomId } from '@/utils/random-utils';
import {
  InspectionEntryFormComponent,
} from '@pages/inspection-detail-page-v2/inspection-entries/inspection-entry-form/inspection-entry-form.component';
import { tryWithLoading } from '@/utils/async-utils';
import { openRepairTaskCreateDialog } from '@components/dialog/repair-task-edit-dialog/repair-task-edit-dialog.component';
import { InspectionContext } from '@pages/inspection-detail-page-v2/inspection.context';
import { watch } from '@/utils/signals/watch';
import RiskLevelEnum = InspectionEntryDetailDto.OccurrenceProbabilityEnum;

export interface InspectionEntryGroup {
  checkpointGroupId: string;
  checkpointGroupName: string;
  checkpointGroupNumber: number;
  entries: InspectionEntryFormType[];
}

const checkpointDetailToItem = (detail: CheckpointDetailDto): CheckpointItemDto => {
  return {
    id: detail.id,
    name: detail.name,
    number: detail.number,
    tenantId: detail.tenant.id,
    checkpointGroupId: detail.checkpointGroup.id,
    checkpointGroupName: detail.checkpointGroup.name,
    checkpointGroupNumber: detail.checkpointGroup.number,
    buildingEquipmentRelated: detail.buildingEquipmentRelated,
    deprecated: detail.deprecated,
    weight: detail.weight,
    permissions: {
      canEdit: true,
    },
  };
};

type MenuItemWithAction = NbMenuItem & { data: { action: () => unknown } };

interface ContextMenu {
  items: MenuItemWithAction[];
  tag: string;
}

const EDITABLE_ELEMENT_SELECTOR = 'input, textarea, select';
const OVERLAY_CONTAINER_SELECTOR = '.cdk-overlay-container';
const VISIBLE_MODAL_BACKDROP_SELECTOR = '.cdk-overlay-backdrop-showing';
const ENTRY_LIST_SCROLLER_SELECTOR = 'nb-card-body.no-padding';

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return target.isContentEditable || target.closest(EDITABLE_ELEMENT_SELECTOR) !== null;
};

const isInsideOverlay = (target: EventTarget | null): boolean => {
  return target instanceof Element && target.closest(OVERLAY_CONTAINER_SELECTOR) !== null;
};

const isModalOpen = (): boolean => {
  return document.querySelector(VISIBLE_MODAL_BACKDROP_SELECTOR) !== null;
};

const waitForRunningAnimations = async (element: Element): Promise<void> => {
  await Promise.allSettled(element.getAnimations({ subtree: true }).map(animation => animation.finished));
};

@Component({
  standalone: false,
  selector: 'wr-inspection-entries[formWrapper][inspection]',
  templateUrl: './inspection-entries.component.html',
  styleUrls: ['./inspection-entries.component.scss'],
  host: {
    '(window:keydown.PageUp)': 'previousEntry($event)',
    '(window:keydown.PageDown)': 'nextEntry($event)',
  },
})
export class InspectionEntriesComponent implements OnInit, OnDestroy {

  readonly inspectionContext = inject(InspectionContext);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);
  private readonly nbMenuService = inject(NbMenuService);
  private readonly dialogService = inject(NbDialogService);
  private readonly checkpointService = inject(CheckpointService);
  private readonly nbToastrService = inject(NbToastrService);
  private readonly nbDialogService = inject(NbDialogService);
  private readonly templateService = inject(TemplateService);
  private readonly repairTaskService = inject(RepairTaskService);
  private readonly buildingSectionService = inject(BuildingSectionService);
  private readonly inspectionEntryService = inject(InspectionEntryService);

  canEdit = signal(false);

  readonly getCheckpointString = getCheckpointString;
  readonly getInspectionEntryStatusFromFormGroup = getInspectionEntryStatusFromFormGroup;
  readonly getInspectionEntryStatusText = getInspectionEntryStatusText;
  readonly isTgaRelevant = isTgaRelevant;

  getEquipmentCount(entry: InspectionEntryFormType): number {
    return entry.controls.buildingEquipments.value?.length ?? 0;
  }

  private loadedSectionIds = new Set<string>();

  entriesLoading = signal(false);

  readonly entryFormComponent = viewChild(InspectionEntryFormComponent);

  readonly formWrapper = input.required<InspectionFormWrapper>();
  readonly inspection = input.required<InspectionDetailDto>();

  @Output()
  importing$ = new BehaviorSubject<boolean>(false);
  @Output()
  repairTaskCreated = new EventEmitter<RepairTaskMergeDto>();

  selectedSection$ = new BehaviorSubject<BuildingSectionDetailDto | undefined>(undefined);
  inspectionEntriesGrouped$ = new BehaviorSubject<InspectionEntryGroup[]>([]);
  selectedEntry$ = new BehaviorSubject<InspectionEntryFormType | undefined>(undefined);
  search$ = new BehaviorSubject<string>('');
  onlyMarkedForPostProcessing$ = new BehaviorSubject<boolean>(false);
  destroy$ = new Subject<void>();
  selectedGroupId?: string;
  loading$ = new BehaviorSubject<boolean>(false);

  sectionContextMenu: ContextMenu = {
    items: [],
    tag: randomId(),
  };
  entryContextMenu: ContextMenu = {
    items: [],
    tag: randomId(),
  };
  entriesThatCanBeAppliedFromOtherSections$ = new BehaviorSubject<InspectionEntryFormType[]>([]);

  get hasAnyPendingAttachments(): boolean {
    return this.entryFormComponent()?.hasAnyPendingAttachments() ?? false;
  }

  constructor() {
    watch(this.inspection, inspection => {
      if (inspection) {
        this.canEdit.set(inspection.permissions.canEdit);
      }
    });
  }

  ngOnInit(): void {
    this.selectedEntry$.pipe(
      takeUntil(this.destroy$),
    ).subscribe(async () => {
      this.entriesThatCanBeAppliedFromOtherSections$.next(await this.getEntriesThatCanBeAppliedFromOtherSections());
    });

    this.nbMenuService.onItemClick().pipe(
      takeUntil(this.destroy$),
      filter(({ tag }) => tag === this.entryContextMenu.tag || tag === this.sectionContextMenu.tag),
    ).subscribe((event) => {
      event.item.data?.action?.();
    });

    this.formWrapper().enabled$.pipe(
      takeUntil(this.destroy$),
      debounceTime(100),
    ).subscribe(editMode => {
      if (!editMode) {
        this.selectedEntry$.next(undefined);
      }
    });

    combineLatest([
      this.selectedEntry$,
      this.formWrapper().enabled$,
    ]).pipe(
      takeUntil(this.destroy$),
    ).subscribe(([entry, editMode]) => {
      this.updateSectionContextMenu(editMode);
      this.updateEntryContextMenu(entry, editMode);
    });

    const inspectionEntries$ = this.formWrapper().form$.pipe(
      takeUntil(this.destroy$),
      filter(form => !!form),
      map(form => form!.controls.inspectionEntries),
      switchMap(inspectionEntries => inspectionEntries.valueChanges.pipe(
        startWith(inspectionEntries.value),
        takeUntil(this.destroy$),
      )),
      shareReplay(1),
    );

    combineLatest([
      this.selectedSection$,
      inspectionEntries$,
      this.search$,
      this.onlyMarkedForPostProcessing$,
    ]).pipe(
      debounceTime(100),
      takeUntil(this.destroy$),
    ).subscribe(async ([section, , search, onlyMarkedForPostProcessing]) => {
      this.updateDisplayedEntries(search, section, onlyMarkedForPostProcessing);
    });

    this.selectedSection$.pipe(
      takeUntil(this.destroy$),
      filter(section => !!section),
      debounceTime(100),
    ).subscribe(async () => {
      await this.createInspectionEntries(this.formWrapper().form()!, this.inspection());
    });
  }

  resetLoadedSections(): void {
    this.loadedSectionIds.clear();
    this.inspectionEntriesGrouped$.next([]);
    this.selectedSection$.next(undefined);
  }

  async createInspectionEntries(form: InspectionFormType, inspection: InspectionDetailDto): Promise<void> {
    const formArray = form.controls?.inspectionEntries;
    if (!formArray) {
      throw new Error('Inspection entries not found');
    }

    const sectionId = this.selectedSection$.value?.id;
    if (!sectionId) {
      throw new Error('No section selected');
    }

    if (this.loadedSectionIds.has(sectionId)) {
      return;
    }
    this.entriesLoading.set(true);
    this.loadedSectionIds.add(sectionId);

    const entries = await firstValueFrom(this.inspectionEntryService.getByInspectionAndSection(
      inspection.id,
      sectionId,
    ));

    for (const entry of entries) {
      const checkpoint = entry.checkpoint;
      const formGroup = createInspectionEntryForm(
        checkpoint,
        { inspectionEntry: entry },
        this.formWrapper().disabled()
      );
      formArray.push(formGroup);
    }
    this.entriesLoading.set(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateDisplayedEntries(search: string, section?: BuildingSectionDetailDto, onlyMarkedForPostProcessing = false) {
    const inspectionEntries = this.formWrapper().form()?.controls.inspectionEntries;

    if (!inspectionEntries) {
      throw new Error('No inspection entries');
    }

    const entryControls = inspectionEntries.controls;
    const entriesInSection = section
      ? entryControls.filter(entry =>
        !entry.controls.markedForDeletion?.value &&
        entry.controls.buildingSectionId?.value === section.id &&
        (!onlyMarkedForPostProcessing || entry.controls.markedForPostProcessing?.value),
      )
      : [];
    const map = new Map<number, InspectionEntryGroup>();
    for (const entry of entriesInSection) {
      const controls = entry.controls;
      const checkpointGroupNumber = controls.checkpointGroupNumber.value ?? 0;
      const checkpointGroupName = controls.checkpointGroupName.value ?? '';
      const checkpointGroupId = controls.checkpointGroupId.value ?? '';
      const entryString = getCheckpointString(entry);

      const filterMatch = entryString.toLowerCase().includes(search.toLowerCase());
      if (filterMatch) {
        const group: InspectionEntryGroup = map.get(checkpointGroupNumber) ?? {
          checkpointGroupNumber,
          checkpointGroupName,
          checkpointGroupId,
          entries: [] as InspectionEntryFormType[],
        };
        group.entries.push(entry);
        map.set(checkpointGroupNumber, group);
      }
    }
    const groups = Array.from(map.values());
    groups.sort((a, b) => a.checkpointGroupNumber - b.checkpointGroupNumber);
    groups.forEach(group =>
      group.entries.sort((a, b) => {
        const checkpointNumberA = a.controls.checkpointNumber.value ?? 0;
        const checkpointNumberB = b.controls.checkpointNumber.value ?? 0;
        return checkpointNumberA - checkpointNumberB;
      }),
    );
    this.inspectionEntriesGrouped$.next(groups);
  }

  async markPostProcessingDone(entry: InspectionEntryFormType): Promise<void> {
    await tryWithLoading(this.loading$, async () => {
      await firstValueFrom(this.inspectionEntryService.markPostProcessingDone(entry.value.id!));
      entry.controls.markedForPostProcessing.setValue(false);
      this.nbToastrService.success('Markierung für die Nachbearbeitung entfernt', 'Erfolg');
    });
  }

  async duplicateCurrent(): Promise<void> {
    console.assert(!!this.selectedEntry$.value, 'No entry selected');
    console.assert(!!this.selectedSection$.value, 'No section selected');
    await this.duplicateEntry(this.selectedEntry$.value!, this.selectedSection$.value!);
  }

  async duplicateEntry(entry: InspectionEntryFormType, targetSection: BuildingSectionDetailDto): Promise<void> {
    const checkpoint = await firstValueFrom(this.checkpointService.findById(entry.value.checkpointId!));
    this.addInspectionEntryByCheckpoint(checkpointDetailToItem(checkpoint), targetSection.id);
    this.nbToastrService.success('Prüfpunkt dupliziert', 'Erfolg');
  }

  async changeCheckpoint(): Promise<void> {
    const currentSection = this.selectedSection$.value?.id;
    console.assert(!!currentSection, 'No entry selected');

    try {
      const checkpointId = await firstValueFrom(this.dialogService.open(CheckpointDialogComponent, {
        context: {
          selectedCheckpointGroupId: this.selectedGroupId,
          selectedCheckpointId: this.selectedEntry$.value?.value?.checkpointId ?? undefined,
        },
      }).onClose);
      if (checkpointId) {
        const controls = this.selectedEntry$.value?.controls;
        const checkpoint = await firstValueFrom(this.checkpointService.findById(checkpointId));
        if (controls?.checkpointId.value !== checkpoint.id) {
          // presets are checkpoint-bound: the select only offers the new checkpoint's presets, so the
          // old one would be sent invisibly and persisted (WR-2380)
          controls?.predefinedAssessment.setValue(undefined);
        }
        controls?.checkpointId.setValue(checkpoint.id);
        controls?.checkpointName.setValue(checkpoint.name);
        controls?.checkpointNumber.setValue(checkpoint.number);
        controls?.checkpointGroupId.setValue(checkpoint.checkpointGroup.id);
        controls?.checkpointGroupName.setValue(checkpoint.checkpointGroup.name);
        controls?.checkpointGroupNumber.setValue(checkpoint.checkpointGroup.number);
        this.selectedGroupId = checkpoint.checkpointGroup.id;
        this.nbToastrService.success('Prüfpunkt geändert', 'Erfolg');
      }
    } catch (err) {
      console.error(err);
      this.nbToastrService.danger('Fehler beim auswählen eines Prüfpunktes', 'Fehler');
    }
  }

  async delete(): Promise<void> {
    if (!await firstValueFrom(this.dialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Prüfpunktzeile löschen',
        message: 'Bist du sicher, dass du die Prüfpunktzeile löschen willst?',
      },
    }).onClose)) {
      return;
    }

    const selected = this.selectedEntry$.value;
    if (!selected) return;

    const inspectionEntries = this.formWrapper().controls()?.inspectionEntries;
    if (!inspectionEntries) {
      throw new Error('No inspection entries');
    }
    selected.controls.markedForDeletion.setValue(true);
    selected.markAsDirty();
    selected.updateValueAndValidity({ emitEvent: true });
    this.selectedEntry$.next(undefined);
  }

  hide(): void {
    this.selectedEntry$.next(undefined);
  }

  async showAddInspectionEntryDialog(): Promise<void> {
    const currentSection = this.selectedSection$.value?.id;

    if (!currentSection) {
      throw new Error('No section selected');
    }

    try {
      const checkpointId = await firstValueFrom(this.dialogService.open(CheckpointDialogComponent, {
        context: {
          selectedCheckpointGroupId: this.selectedGroupId,
        },
      }).onClose);
      if (checkpointId) {
        const checkpoint = await firstValueFrom(this.checkpointService.findById(checkpointId));
        this.addInspectionEntryByCheckpoint(checkpointDetailToItem(checkpoint), currentSection);
        this.nbToastrService.success('Prüfpunktzeile hinzugefügt', 'Erfolg');
      }
    } catch (err) {
      console.error(err);
      this.nbToastrService.danger('Fehler beim auswählen eines Prüfpunktes', 'Fehler');
    }
  }

  addInspectionEntryByCheckpoint(checkpoint: CheckpointItemDto, targetSectionId: string): void {
    const inspectionEntries = this.formWrapper().controls()?.inspectionEntries;

    if (!inspectionEntries) {
      throw new Error('No inspection entries');
    }

    const formGroup = createInspectionEntryForm(checkpoint, {
      targetSectionId,
      standardAssessment: checkpoint.standardAssessment,
    }, this.formWrapper().disabled());

    inspectionEntries.push(formGroup);
    this.formWrapper().updateValueAndValidity({ emitEvent: false });
    this.selectedEntry$.next(formGroup);
    this.selectedGroupId = checkpoint.checkpointGroupId;
  }

  async importTemplate(): Promise<void> {
    const templateSectionResult = await firstValueFrom(this.nbDialogService.open(TemplateSelectionDialogComponent, {
      context: {
        buildingSections: this.inspection().building.sections,
      },
    }).onClose);
    await tryWithLoading(this.importing$, async () => {
      if (templateSectionResult.templateId) {
        const template = await firstValueFrom(this.templateService.findById(templateSectionResult.templateId));
        const templateCheckpoints = [...template.templateCheckpoints]
          .sort((a, b) => a.orderSequence - b.orderSequence);

        for (const templateCheckpoint of templateCheckpoints) {
          for (const sectionId of templateSectionResult.buildingSectionIds) {
            const checkpointId = templateCheckpoint.checkpoint.id;

            if (!this.checkpointExists(checkpointId, sectionId)) {
              this.addInspectionEntryByCheckpoint(templateCheckpoint.checkpoint, sectionId);
            }
          }
        }
      }
    });
  }

  private checkpointExists(checkpointId: string, sectionId: string): boolean {
    const inspectionEntries = this.formWrapper().form()?.controls.inspectionEntries.controls;

    if (!inspectionEntries) {
      return false;
    }

    return inspectionEntries.some(entry => {
      const entryCheckpointId = entry.controls.checkpointId?.value;
      const entrySectionId = entry.controls.buildingSectionId?.value;
      return entryCheckpointId === checkpointId && entrySectionId === sectionId;
    });
  }

  private updateSectionContextMenu(editMode: boolean): void {
    const items: MenuItemWithAction[] = [];

    if (editMode) {
      items.push(...[
        {
          title: 'Gebäudeteil duplizieren',
          icon: { pack: 'eva', icon: 'diagonal-arrow-right-up-outline' },
          data: {
            action: () => this.duplicateSection(),
          },
        },
        {
          title: 'Leere Prüfpunktzeilen löschen',
          icon: { pack: 'eva', icon: 'trash-2-outline' },
          data: { action: () => this.showDeleteEmptyInspectionsEntriesDialog() },
        },
      ]);
    }
    this.sectionContextMenu.items = items;
  }

  private updateEntryContextMenu(currentEntry: InspectionEntryFormType | undefined, editMode: boolean): void {
    const menu: MenuItemWithAction[] = [];

    const selectedEntryId = currentEntry?.value?.id;
    if (!this.canEdit()) {
      return;
    }

    if (selectedEntryId && this.inspectionContext.repairTasksVisible()) {
      if (!this.inspectionContext.hasRepairTask(selectedEntryId)) {
        menu.push({
          title: 'Reparaturauftrag erstellen',
          icon: { pack: 'eva', icon: 'radio-button-on-outline' },
          data: { action: () => this.createRepairTask(selectedEntryId) },
        });
      }

      if (this.inspectionContext.hasRepairTask(selectedEntryId)) {
        menu.push({
          title: 'Reparaturauftrag löschen',
          icon: { pack: 'eva', icon: 'trash-2-outline' },
          data: { action: () => this.deleteRepairTask(selectedEntryId) },
        });
      }
    }

    if (editMode) {
      menu.push(
        {
          title: 'Duplizieren',
          icon: { pack: 'eva', icon: 'copy' },
          data: { action: () => this.duplicateCurrent() },
        },
        {
          title: 'Prüfpunkt ändern',
          icon: { pack: 'eva', icon: 'repeat' },
          data: { action: () => this.changeCheckpoint() },
        },
        {
          title: 'Löschen',
          icon: { pack: 'eva', icon: 'trash-2-outline' },
          data: { action: () => this.delete() },
        },
      );
    }

    menu.push({
      title: 'Ausblenden',
      icon: { pack: 'eva', icon: 'eye-off-outline' },
      data: { action: () => this.hide() },
    });

    this.entryContextMenu.items = menu;
  }


  async applyEntryFromOtherSection(): Promise<void> {
    const entries = this.entriesThatCanBeAppliedFromOtherSections$.value;
    const selected = await this.showApplyEntryFromOtherSectionDialog(entries);
    if (selected) {
      const controls = this.selectedEntry$.value?.controls;
      controls?.predefinedAssessment?.setValue(selected.controls.predefinedAssessment?.value);
      controls?.action?.setValue(selected.controls.action?.value);
      controls?.note?.setValue(selected.controls.note?.value);
      controls?.deadline?.setValue(selected.controls.deadline?.value);
      controls?.occurrenceProbability?.setValue(selected.controls.occurrenceProbability?.value);
      controls?.damageExtent?.setValue(selected.controls.damageExtent?.value);
      this.selectedEntry$.value?.markAsDirty();
      this.selectedEntry$.value?.updateValueAndValidity({ emitEvent: false });
    }
  }

  private findEntriesWithCheckpointInOtherSection(checkpointId: string, currentSectionId: string): InspectionEntryFormType[] {
    const allEntries = this.formWrapper().controls()?.inspectionEntries;
    return allEntries?.controls.filter(entry =>
      entry.controls.checkpointId?.value === checkpointId &&
      entry.controls.buildingSectionId?.value !== currentSectionId &&
      entry.controls.predefinedAssessment.value,
    ) ?? [];
  }

  private async showApplyEntryFromOtherSectionDialog(entries: InspectionEntryFormType[]): Promise<InspectionEntryFormType | undefined> {
    if (entries?.length) {
      return await firstValueFrom(this.nbDialogService.open(ApplyFromOtherSectionDialogComponent, {
        context: {
          building: this.formWrapper().controls()?.building.value,
          entries,
          checkpointString: getCheckpointString(this.selectedEntry$.value!),
        },
      }).onClose);
    }
    return undefined;
  }

  private async getEntriesThatCanBeAppliedFromOtherSections(): Promise<InspectionEntryFormType[]> {
    const controls = this.selectedEntry$.value?.controls;
    const predefinedAssessment = controls?.predefinedAssessment.value;
    if (predefinedAssessment) {
      return [];
    }
    const checkpointId = controls?.checkpointId?.value;
    const sectionId = this.selectedSection$.value?.id;

    if (!checkpointId || !sectionId) {
      return [];
    }

    return this.findEntriesWithCheckpointInOtherSection(checkpointId, sectionId);
  }

  async deleteGroup(groupToDelete: InspectionEntryGroup): Promise<void> {
    const inspectionEntries = this.formWrapper().controls()?.inspectionEntries;

    if (!inspectionEntries) {
      throw new Error('No inspection entries');
    }

    const entries = inspectionEntries.controls;
    const sectionId = this.selectedSection$.value?.id;

    const entriesToDelete = entries.filter(entry =>
      entry.value.checkpointGroupId === groupToDelete.checkpointGroupId &&
      entry.value.buildingSectionId === sectionId,
    );

    console.assert(entriesToDelete.length > 0, 'No entries to delete');

    const confirmed = await firstValueFrom(this.nbDialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Gruppe löschen?',
        message: entriesToDelete.length > 1
          ? `Alle ${entriesToDelete.length} Prüfpunktzeilen in dieser Gruppe werden gelöscht.`
          : '1 Prüfpunktzeile wird gelöscht.',
      },
    }).onClose,
    );

    if (!confirmed) return;

    for (const entry of entriesToDelete) {
      entry.controls.markedForDeletion.setValue(true);
      entry.markAsDirty();
      entry.updateValueAndValidity({ emitEvent: false });
    }
    this.formWrapper().updateValueAndValidity({ emitEvent: true });
    this.selectedEntry$.next(undefined);
  }

  private async duplicateSection(): Promise<void> {
    const inspectionEntries = this.formWrapper().controls()?.inspectionEntries;

    if (!inspectionEntries) {
      throw new Error('No inspection entries');
    }

    const entries = inspectionEntries.controls;
    const entriesInSection = entries.filter(entry => entry.value.buildingSectionId === this.selectedSection$.value?.id);
    const result = await firstValueFrom(this.nbDialogService.open(DuplicateSectionDialogComponent, {
      context: {
        entries: entriesInSection,
        building: this.formWrapper().controls()?.building.value,
      },
    }).onClose) as DuplicateSectionDialogResult;
    if (result) {
      result.entriesToDuplicate.forEach(entry => {
        this.duplicateEntry(entry, result.targetBuildingSection);
      });
      this.nbToastrService.success('Prüfpunktzeilen dupliziert', 'Erfolg');
      this.selectedSection$.next(result.targetBuildingSection);
      this.selectedEntry$.next(undefined);
    }
  }

  async showDeleteEmptyInspectionsEntriesDialog(): Promise<void> {
    const inspectionEntries = this.formWrapper().controls()?.inspectionEntries;
    if (!inspectionEntries) {
      throw new Error('No inspection entries');
    }

    const { emptyEntries, emptyEntriesCount } =
      this.findEmptyOrNullEntries(inspectionEntries.controls);

    const userConfirmed = await firstValueFrom(
      this.nbDialogService.open(DeleteDialogComponent, {
        context: {
          title: 'Leere Prüfzeilen löschen',
          message: `Bist du sicher, dass du ${emptyEntriesCount} leere Einträge löschen willst?`,
        },
      }).onClose,
    );

    if (!userConfirmed) {
      return;
    }

    for (const entry of emptyEntries) {
      entry.controls.markedForDeletion.setValue(true);
      entry.markAsDirty();
      entry.updateValueAndValidity({ emitEvent: false });
    }

    inspectionEntries.updateValueAndValidity({ emitEvent: true });
  }

  private findEmptyOrNullEntries(entries: InspectionEntryFormType[]) {
    const isNullOrEmpty = (value: string | AssessmentPresetItemDto | ConfiguredActionItemDto | null | undefined) => value === null || value === undefined || value === '';
    const hasNoFinding = (value: string | null | undefined) => isNullOrEmpty(value) || value === RiskLevelEnum.NoFinding;
    const emptyEntries = entries.filter(entry =>
      isNullOrEmpty(entry.value.deadline) &&
      isNullOrEmpty(entry.value.predefinedAssessment) &&
      isNullOrEmpty(entry.value.action) &&
      isNullOrEmpty(entry.value.note) &&
      hasNoFinding(entry.value.occurrenceProbability) &&
      hasNoFinding(entry.value.damageExtent),
    );
    const emptyEntriesCount = emptyEntries.length;
    return { emptyEntries, emptyEntriesCount };
  }

  private async deleteRepairTask(entryId: string) {
    const repairTask = await firstValueFrom(this.repairTaskService.findByInspectionEntryId(entryId));
    if (!repairTask) {
      this.nbToastrService.info('Es gibt für diesen Prüfpunkt keinen Reparaturauftrag!', 'Reparaturauftrag nicht vorhanden!');
      return;
    }

    const userConfirmed = await firstValueFrom(this.nbDialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Reparaturauftrag löschen',
        message: 'Bist du sicher, dass du den Reparaturauftrag löschen willst?',
      },
    }).onClose);

    if (userConfirmed) {
      await tryWithLoading(this.loading$, async () => {
        await firstValueFrom(this.repairTaskService.deleteById(repairTask.id));
        this.nbToastrService.success('Reparaturauftrag gelöscht', 'Erfolg');
        this.repairTaskCreated.emit();
      });
    }
  }

  private async createRepairTask(entryId: string) {
    const repairTask = await firstValueFrom(this.repairTaskService.findByInspectionEntryId(entryId));
    if (repairTask) {
      this.nbToastrService.info('Es gibt für diesen Prüfpunkt bereits einen Reparaturauftrag!', 'Reparaturauftrag vorhanden!');
      return;
    }

    const value = this.selectedEntry$.value?.value;
    const checkpointId = value?.checkpointId;
    if (!checkpointId) {
      throw new Error('No checkpoint id');
    }

    const created = await openRepairTaskCreateDialog(this.nbDialogService, {
      buildingId: this.inspection().building.id,
      inspectionEntryId: entryId,
      checkpointId,
      title: value.predefinedAssessment?.text,
      description: value.note,
    });
    if (created) {
      this.repairTaskCreated.emit();
    }
  }

  async editRepairTask(entry: InspectionEntryFormType): Promise<void> {
    const inspectionEntryId = entry.value.id;

    if (!inspectionEntryId) {
      throw new Error('InspectionEntryId is missing');
    }
    const repairTask = this.inspectionContext.getRepairTaskForEntry(inspectionEntryId);

    if (!repairTask) {
      throw new Error('RepairTask not found');
    }

    return await this.inspectionContext.editRepairTask(repairTask);
  }

  async navigateToEntry(entryId: string): Promise<void> {
    const allEntries = this.formWrapper().controls()?.inspectionEntries.controls;
    const targetEntry = allEntries?.find(entry => entry.controls.id.value === entryId);

    if (!targetEntry) {
      this.nbToastrService.danger('Fehler beim Navigieren zu Prüfpunktzeile', 'Fehler');
      throw new Error('Entry not found');
    }

    const sectionId = targetEntry.controls.buildingSectionId.value;
    const groupId = targetEntry.controls.checkpointGroupId.value;

    if (!sectionId) {
      this.nbToastrService.danger('Fehler beim Navigieren zu Prüfpunktzeile', 'Fehler');
      throw new Error('No section id');
    }

    const targetSection = await firstValueFrom(this.buildingSectionService.findById(sectionId));
    if (targetSection) {
      this.selectedSection$.next(targetSection);
      this.selectedGroupId = groupId;
      this.selectedEntry$.next(targetEntry);
    }
  }

  previousEntry(event: KeyboardEvent) {
    this.handleEntryNavigationKey(event, -1);
  }

  nextEntry(event: KeyboardEvent) {
    this.handleEntryNavigationKey(event, 1);
  }

  private handleEntryNavigationKey(event: KeyboardEvent, indexOffset: number) {
    if (!this.shouldHandleEntryNavigationKey(event)) {
      return;
    }
    event.preventDefault();
    this.jumpInspectionEntry(indexOffset);
  }

  private shouldHandleEntryNavigationKey(event: KeyboardEvent): boolean {
    return this.isVisible()
      && !isEditableTarget(event.target)
      && !isInsideOverlay(event.target)
      && !isModalOpen();
  }

  private isVisible(): boolean {
    return this.elementRef.nativeElement.checkVisibility();
  }

  private jumpInspectionEntry(indexOffset: number) {
    const entries = this.flattenInspectionEntries();
    const currentEntry = this.selectedEntry$.value;
    const currentIndex = entries.findIndex((entry) => entry.value.cid === currentEntry?.value.cid);
    const nextIndex = currentIndex + indexOffset;
    if (nextIndex >= 0 && nextIndex < entries.length) {
      const entry = entries[nextIndex];
      this.selectedEntry$.next(entry);
      this.selectedGroupId = entry.value.checkpointGroupId;
      this.scrollToEntry();
    }
  }

  private flattenInspectionEntries(): InspectionEntryFormType[] {
    return this.inspectionEntriesGrouped$.value.flatMap(group => group.entries);
  }

  private scrollToEntry() {
    afterNextRender(() => void this.scrollToEntryAfterAccordionAnimation(), { injector: this.injector });
  }

  private async scrollToEntryAfterAccordionAnimation() {
    const scroller = this.elementRef.nativeElement.querySelector<HTMLElement>(ENTRY_LIST_SCROLLER_SELECTOR);
    if (!scroller) {
      return;
    }
    await waitForRunningAnimations(scroller);
    this.centerSelectedEntryIn(scroller);
  }

  private centerSelectedEntryIn(scroller: HTMLElement) {
    const cid = this.selectedEntry$.value?.value.cid;
    if (!cid) {
      return;
    }
    const element = document.getElementById(cid);
    if (!element || !scroller.contains(element)) {
      return;
    }
    const elementRect = element.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    scroller.scrollTop += elementRect.top - scrollerRect.top - (scrollerRect.height - elementRect.height) / 2;
  }
}
